import "./UniversalEditModal.scss";
import Modal from "../UI/Modal/Modal";
import ProfileImages from "./components/ProfileImages";
import Sidebar from "./components/Sidebar";
import ProfileForm from "./components/Form";
import { UseFormHandleSubmit, SubmitHandler } from "react-hook-form";
import { useState, useEffect } from "react";
import { SetURLSearchParams } from "react-router";

interface UniversalEditModalProps {
    isOpen: boolean;
    closeModal: () => void;
    themeUrl: string;
    avatarUrl: string;
    themeInputRef: React.RefObject<HTMLInputElement>;
    avatarInputRef: React.RefObject<HTMLInputElement>;
    handleFile: (
        type: "avatar" | "theme",
    ) => (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
    activeTab: string;
    changeActiveTab: (tab: string) => void;
    handleSubmit: UseFormHandleSubmit<any>;
    onSubmit: SubmitHandler<any>;
    handleAvatarUpload: (file: File) => Promise<void>;
    setSearchParams?: SetURLSearchParams;
    tabs: {
        id: string;
        label: string;
        component: React.ReactNode;
    }[];
}

export default function UniversalEditModal({
    isOpen,
    closeModal,
    themeUrl,
    avatarUrl,
    themeInputRef,
    avatarInputRef,
    handleFile,
    tabs,
    activeTab,
    changeActiveTab,
    handleSubmit,
    onSubmit,
    setSearchParams,
    handleAvatarUpload,
}: UniversalEditModalProps) {
    const [mobileView, setMobileView] = useState<"tabs" | "form">("tabs");
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 913);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 913);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (isMobile && params.get("tab")) {
            setMobileView("form");
        }
    }, [isMobile]);

    const handleBackClick = () => {
        setMobileView("tabs");
        if (setSearchParams)
            setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);
                newParams.delete("tab");
                return newParams;
            });
    };

    const trigger = (r: React.RefObject<HTMLInputElement>) =>
        r.current?.click();

    return (
        <Modal
            isOpen={isOpen}
            closeModal={closeModal}
            classes={{
                modal: "universaleditmodal",
                closeButton: `universaleditmodal_closeButton ${mobileView === "form" ? "hidden" : ""}`,
            }}
        >
            {isMobile ? (
                mobileView === "tabs" ? (
                    <div className="universaleditmodal-left">
                        <ProfileImages
                            themeUrl={themeUrl}
                            avatarUrl={avatarUrl}
                            triggerThemeInput={() => trigger(themeInputRef)}
                            triggerAvatarInput={() => trigger(avatarInputRef)}
                            themeInputRef={themeInputRef}
                            avatarInputRef={avatarInputRef}
                            handleThemeChange={handleFile("theme")}
                            handleAvatarUpload={handleAvatarUpload}
                        />

                        <Sidebar
                            tabs={tabs}
                            activeTab={activeTab}
                            changeActiveTab={(tab) => {
                                changeActiveTab(tab);
                                setMobileView("form");
                            }}
                        />
                    </div>
                ) : (
                    <>
                        <button
                            className="universaleditmodal_backButton"
                            onClick={handleBackClick}
                        >
                            <svg
                                width="36"
                                height="36"
                                viewBox="0 0 36 36"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M7.5 18H28.5M7.5 18L16.5 27M7.5 18L16.5 9"
                                    stroke="white"
                                    stroke-width="3"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </button>
                        <ProfileForm
                            activeTab={activeTab}
                            tabs={tabs}
                            handleSubmit={handleSubmit}
                            onSubmit={onSubmit}
                        />
                    </>
                )
            ) : (
                <>
                    <div className="universaleditmodal-left">
                        <ProfileImages
                            themeUrl={themeUrl}
                            avatarUrl={avatarUrl}
                            triggerThemeInput={() => trigger(themeInputRef)}
                            triggerAvatarInput={() => trigger(avatarInputRef)}
                            themeInputRef={themeInputRef}
                            avatarInputRef={avatarInputRef}
                            handleThemeChange={handleFile("theme")}
                            handleAvatarUpload={handleAvatarUpload}
                        />

                        <Sidebar
                            tabs={tabs}
                            activeTab={activeTab}
                            changeActiveTab={changeActiveTab}
                        />
                    </div>

                    <ProfileForm
                        activeTab={activeTab}
                        tabs={tabs}
                        handleSubmit={handleSubmit}
                        onSubmit={onSubmit}
                    />
                </>
            )}
        </Modal>
    );
}
